/**
 * Central navigation config for app shell (sidebar, app bar) and page headers.
 * Use filterNavItems() for adminOnly / visible flags before rendering.
 */

export const NAV_GROUPS = {
  PRIMARY: 'primary',
  DIRECTORIES: 'directories',
  MY_ITEMS: 'myItems',
  FOOTER: 'footer',
};

/** Top-level sidebar / primary links */
export const primaryNav = [
  {
    id: 'dashboard',
    text: 'Dashboard',
    icon: 'mdi-view-dashboard',
    route: 'home',
    group: NAV_GROUPS.PRIMARY,
    order: 1,
  },
  {
    id: 'saved-searches',
    text: 'Saved Searches',
    icon: 'mdi-view-headline',
    group: NAV_GROUPS.PRIMARY,
    order: 2,
    action: 'openSavedSearchesDrawer',
  },
  {
    id: 'search-profiles',
    text: 'Search Profiles',
    icon: 'mdi-magnify-scan',
    route: 'search-profiles',
    group: NAV_GROUPS.PRIMARY,
    order: 3,
  },
  {
    id: 'marketplace',
    text: 'Marketplace',
    icon: 'mdi-storefront-outline',
    // Placeholder until Reports Marketplace launches: non-clickable + "soon" label (NavigationDrawerNavItem).
    // To enable: remove disabled/soon and set href to the reports marketplace URL.
    disabled: true,
    soon: true,
    group: NAV_GROUPS.PRIMARY,
    order: 4,
  },
];

/** Directories dropdown / sidebar section */
export const directoriesNav = [
  {
    id: 'buyers',
    text: 'Buyers',
    icon: '',
    route: 'entities',
    group: NAV_GROUPS.DIRECTORIES,
    order: 1,
  },
  {
    id: 'suppliers',
    text: 'Suppliers',
    icon: '',
    route: 'suppliers',
    group: NAV_GROUPS.DIRECTORIES,
    order: 2,
  },
  {
    id: 'categories',
    text: 'Categories',
    icon: '',
    route: 'categories',
    group: NAV_GROUPS.DIRECTORIES,
    order: 3,
  },
  {
    id: 'panels',
    text: 'Panels',
    icon: '',
    route: 'panels',
    group: NAV_GROUPS.DIRECTORIES,
    order: 4,
  },
  {
    id: 'audits',
    text: 'Audits',
    icon: '',
    route: 'audits',
    group: NAV_GROUPS.DIRECTORIES,
    order: 5,
  },
];

/** My Items dropdown / sidebar section */
export const myItemsNav = [
  {
    id: 'reports',
    text: 'Reports',
    icon: '',
    route: 'storyboards',
    group: NAV_GROUPS.MY_ITEMS,
    order: 1,
  },
  {
    id: 'supplier-clusters',
    text: 'Supplier Clusters',
    icon: '',
    route: 'clusters',
    query: { entity_type: 'supplier' },
    group: NAV_GROUPS.MY_ITEMS,
    order: 2,
  },
  {
    id: 'buyer-clusters',
    text: 'Buyer Clusters',
    icon: '',
    route: 'clusters',
    query: { entity_type: 'publisher' },
    group: NAV_GROUPS.MY_ITEMS,
    order: 3,
  },
  {
    id: 'admin-portal',
    text: 'Admin Portal',
    icon: '',
    route: 'admin-portal',
    group: NAV_GROUPS.MY_ITEMS,
    order: 4,
    adminOnly: true,
  },
  {
    id: 'pipelines',
    text: 'Pipelines',
    subtitle: '(Prospect Lists)',
    icon: '',
    route: 'pipelines',
    group: NAV_GROUPS.MY_ITEMS,
    order: 5,
  },
];

/** Footer sidebar items (e.g. Help) */
export const footerNav = [
  {
    id: 'help',
    text: 'Help',
    icon: 'mdi-help-circle',
    group: NAV_GROUPS.FOOTER,
    order: 1,
    /** Opens right utility drawer tab; see utilityDrawerNav */
    drawerTab: 3,
    action: 'openUtilityDrawer',
  },
];

/**
 * Right-drawer tabs (Tracey, Profile). Help uses HelpDrawer (drawerTab 3).
 * drawerTab index matches App.vue drawer_tabs / v-tab-item order.
 */
export const utilityDrawerNav = [
  {
    id: 'utility-tracey',
    text: 'Tracey',
    icon: 'mdi-creation',
    drawerTab: 0,
    order: 1,
    action: 'openUtilityDrawer',
    highlightIcon: true,
  },
  {
    id: 'utility-profile',
    text: 'My Profile',
    drawerTab: 1,
    order: 2,
    action: 'openUtilityDrawer',
  },
  {
    id: 'utility-help',
    text: 'Help',
    icon: 'mdi-help-circle',
    drawerTab: 3,
    order: 3,
    action: 'openUtilityDrawer',
  },
];

/** Route name → header title (AppHeader / sidebar active state) */
export const routePageTitles = {
  home: 'Dashboard',
  entities: 'Buyers',
  suppliers: 'Suppliers',
  categories: 'Categories',
  panels: 'Panels',
  audits: 'Audits',
  storyboards: 'Reports',
  clusters: 'Clusters',
  'admin-portal': 'Admin Portal',
  'admin-storyboards': 'Storyboard Management',
  'admin-saved-searches': 'Saved Search Management',
  'admin-monitoring': 'Monitoring',
  'cluster-administration': 'Cluster Administration',
  pipelines: 'Pipelines',
  'search-profiles': 'Search Profiles',
  search: 'Search',
};

/**
 * @param {object} item - Nav item with route and/or path and optional query
 * @returns {string|object} vue-router `to` location
 */
export function getNavRouteLocation(item) {
  if (!item) return '/';
  if (item.path) return item.path;
  if (item.route) {
    const location = { name: item.route };
    if (item.query) location.query = item.query;
    return location;
  }
  return '/';
}

/**
 * @param {object[]} items
 * @param {{ isAdmin?: boolean }} [context]
 * @returns {object[]}
 */
export function filterNavItems(items, context = {}) {
  const { isAdmin = false } = context;
  return [...items]
    .filter((item) => item.visible !== false)
    .filter((item) => !item.adminOnly || isAdmin)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * @param {string} [routeName]
 * @returns {string|null}
 */
export function getPageTitle(routeName) {
  if (!routeName) return null;
  return routePageTitles[routeName] ?? null;
}

/**
 * @param {string} [routeName]
 * @returns {boolean}
 */
export function isDashboardRoute(routeName) {
  return routeName === 'home';
}

/**
 * @param {object} item - Nav item with route and optional query
 * @param {{ name?: string, query?: object }} route - vue-router route object
 * @returns {boolean}
 */
export function isNavItemActive(item, route) {
  if (!item?.route || !route?.name) return false;
  if (route.name !== item.route) return false;
  if (item.query) {
    return Object.entries(item.query).every(
      ([key, value]) => route.query?.[key] === String(value)
    );
  }
  return true;
}
